import {
  Avatar,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { InformationModal } from 'utoniq-ui'

import {
  ArtistFavoriteMember,
  Avatar as AvatarType,
  FavoriteMembersSelectWithPatronQuery,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { MembershipFavoriteMemberSelect } from '@/components/organisms/membership/membershipFavoriteMembersSelect'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { CellFrame, Template } from '@/core/architecture'

export type State = {
  artist: Maybe<FavoriteMembersSelectWithPatronQuery['artist']>
  artistId: string
  nextChangeableDate: string
  isPatron: boolean
  selectedFavoriteMember?: string
  initialSelectedFavoriteMember?: string
  favoriteMembers:
    | Maybe<
        Maybe<
          Pick<ArtistFavoriteMember, 'name' | 'id'> & {
            avatar: Maybe<Pick<AvatarType, 'uri'>>
          }
        >[]
      >
    | undefined
  initialLoading: boolean
  updatingMember: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onConfirm: () => Promise<void>
  onSelectFavoriteMember: (memberId: string) => void
}

export const FavoriteMembersSelectTemplate: Template<State, Action> = ({
  state,
  onConfirm,
  onSelectFavoriteMember,
}) => {
  const { t } = useTranslation('favoriteMembersSelect')
  const { router, routerNames, routerPages } =
    useRouter<{ artistId: string; productId: string }>()
  const defaultBackPage = generatePath(routerPages.MyProfile)

  const [openChangeFavoriteMemberModal, setOpenChangeFavoriteMemberModal] =
    useState<boolean>(false)

  const isInitialSelectedFavoriteMember = useMemo(() => {
    return (
      !!state.initialSelectedFavoriteMember &&
      state.initialSelectedFavoriteMember !== ''
    )
  }, [state.initialSelectedFavoriteMember])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.FavoriteMembersSelect} />
      <BaseLayout
        header={{
          back: false,
          defaultBackPage: defaultBackPage,
          title: t('title', '推し設定'),
        }}
      >
        <Card elevation={0} sx={{ borderRadius: 2, bgcolor: 'grey.50', m: 5 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="center">
              <Avatar
                src={state.artist?.avatar?.uri ?? ''}
                sx={{ width: 44, height: 44, m: 2 }}
              />
              <Typography variant="body1" sx={{ width: 'fit-content' }}>
                {state.artist?.displayName}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
        <CellFrame sx={{ m: 5, height: '320px', overflowY: 'auto' }}>
          <MembershipFavoriteMemberSelect
            favoriteMembers={state.favoriteMembers ?? []}
            selectedFavoriteMember={state.selectedFavoriteMember}
            onSelectFavoriteMember={onSelectFavoriteMember}
          />
        </CellFrame>
        <Stack sx={{ alignItems: 'center' }}>
          <CellFrame
            sx={{
              mx: 5,
              mb: 3,
              p: 2,
              border: 'solid 1px',
              borderColor: 'info.main',
              width: '50%',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="info.main">
              {t('note', '※ 推しは設定から90日間変更できません')}
            </Typography>
          </CellFrame>
        </Stack>
        {state.nextChangeableDate !== '' && (
          <CellFrame sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <Typography variant="caption" color="text.primary">
              {t('nextChangeableDate', '次回変更可能日 : {{-date}}', {
                date: state.nextChangeableDate,
              })}
            </Typography>
          </CellFrame>
        )}
        <Stack sx={{ my: 4 }} alignItems="center" gap={4}>
          <Button
            size="large"
            disabled={
              !!state.nextChangeableDate ||
              !state.isPatron ||
              !state.selectedFavoriteMember
            }
            loading={state.initialLoading || state.updatingMember}
            sx={{ width: 212 }}
            onClick={() => setOpenChangeFavoriteMemberModal(true)}
          >
            {t('submit', '推しを選択')}
          </Button>
          {/*
            @note
            アーティストが推し設定をONにしている場合、
            メンバーシップ加入中ユーザーは推しの選択が必須のため、
            初期状態で推しメンバー未選択時に戻るボタンを表示させない。
          */}
          {isInitialSelectedFavoriteMember && (
            <Button
              variant="text"
              size="small"
              sx={{ width: 212 }}
              onClick={() => router.push(routerPages.MyProfile)}
            >
              {t('back', '戻る')}
            </Button>
          )}
        </Stack>
      </BaseLayout>
      <InformationModal
        open={openChangeFavoriteMemberModal}
        onClose={() => setOpenChangeFavoriteMemberModal(false)}
        title={t('changeFavoriteMember', '推しを変更しますか？')}
        cancelText={t('changeFavoriteMemberCancel', '閉じる')}
        content={
          <Stack sx={{ pb: 8, px: 2, alignItems: 'center' }}>
            <Typography variant="body2">
              {t(
                'changeFavoriteMemberDescription',
                '推しメンバーを変更すると、設定中の推し日も新しい日付に変更されます。前の推し日に戻すことはできません。'
              )}
            </Typography>
          </Stack>
        }
        buttons={
          <Button
            size="large"
            color="primary"
            loading={state.updatingMember}
            sx={{ width: 212 }}
            onClick={() => {
              setOpenChangeFavoriteMemberModal(false)
              onConfirm()
            }}
          >
            {t('changeFavoriteMemberConfirm', '変更する')}
          </Button>
        }
      />
    </ArtistThemeProvider>
  )
}
